import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ArrowRight } from 'lucide-react';

import { openDirectMessageByKey } from '../../lib/directMessageNavigation';
import { logError } from '../../lib/logger';

import NewChatGroupImage from '../../../assets/images/new-chat-group.png';

import PeoplePickerPanel from './PeoplePickerPanel';

const NOOP = () => {};

const NewGroupRow = React.memo(function NewGroupRow({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-20 w-full cursor-pointer items-center justify-between gap-3 rounded-lg border-[0.5px] border-white bg-gray-50 p-4 text-left outline-none hover:bg-white focus-visible:ring-2 focus-visible:ring-gray-400"
    >
      <span className="flex min-w-0 items-center gap-3">
        <img src={NewChatGroupImage} alt="" className="size-12 shrink-0 rounded-full border-[0.5px] border-black/10 object-cover" />
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-base leading-5 font-medium text-black">Create a space</span>
          <span className="truncate text-xs leading-4 text-gray-400">then add people after</span>
        </span>
      </span>
      {/* Visual affordance only — the whole card is the button. */}
      <span className="flex h-9 w-[86px] shrink-0 items-center justify-center rounded-full bg-idle-blue text-white hover:opacity-90">
        <ArrowRight className="size-5" strokeWidth={2} absoluteStrokeWidth />
      </span>
    </button>
  );
});

const NewMessagePanel = ({ onMessageOpened = NOOP, onNewGroupSelected = NOOP }) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [startingUserId, setStartingUserId] = useState(null);
  const isMountedRef = useRef(true);

  const handleSelectUser = useCallback(
    async function selectMessageableUser(user) {
      if (!user?.direct_message_key || startingUserId) return;

      setErrorMessage(null);
      setStartingUserId(user.id);

      try {
        const directMessage = await openDirectMessageByKey(user.direct_message_key);
        if (!directMessage?.id) throw new Error('Direct message payload missing id');
        onMessageOpened();
      } catch (error) {
        logError('NewMessagePanel', 'Failed to open message', { userId: user.id, error });
        if (isMountedRef.current) setErrorMessage('Could not open message. Please try again.');
      } finally {
        if (isMountedRef.current) setStartingUserId(null);
      }
    },
    [onMessageOpened, startingUserId],
  );

  const handleNewGroupClick = useCallback(
    function handleNewGroupClickCallback() {
      onNewGroupSelected();
    },
    [onNewGroupSelected],
  );

  useEffect(function trackMountedState() {
    isMountedRef.current = true;
    return function markUnmounted() {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <PeoplePickerPanel
      onSelectUser={handleSelectUser}
      actionUserId={startingUserId}
      actionLabel="Opening"
      disabled={Boolean(startingUserId)}
      externalErrorMessage={errorMessage}
      leadingContent={<NewGroupRow onClick={handleNewGroupClick} />}
    />
  );
};

export default NewMessagePanel;
